import { useState } from "react";
import { Application, generateId, AppSettings, CURRENCY_SYMBOLS } from "@/lib/storage";
import { Briefcase, ExternalLink, Plus, Loader2, Link2, X, Trash2, Pencil, Check, Building2, Home, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  applications: Application[];
  settings: AppSettings;
  onAdd: (app: Application) => void;
  onRemove: (id: string) => void;
  onUpdateStatus: (id: string, status: Application["status"]) => void;
  onUpdateApp: (id: string, fields: Partial<Application>) => void;
}

const STATUS_COLORS: Record<Application["status"], string> = {
  applied: "bg-primary/10 text-primary",
  interviewing: "bg-accent/20 text-accent-foreground",
  offered: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-destructive/10 text-destructive",
};

const STATUS_OPTIONS: Application["status"][] = ["applied", "interviewing", "offered", "rejected"];

export default function ApplicationsTab({ applications, settings, onAdd, onRemove, onUpdateStatus, onUpdateApp }: Props) {
  const [showInput, setShowInput] = useState(false);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCompany, setEditCompany] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editingCtcId, setEditingCtcId] = useState<string | null>(null);
  const [editCtc, setEditCtc] = useState("");

  const handleExtract = async () => {
    if (!url.trim()) return;
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("extract-job", {
        body: { url: url.trim() },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      const company = data.company || "";
      const position = data.position || "";

      const app: Application = {
        id: generateId(),
        company: company || "Unknown Company",
        position: position || "Unknown Position",
        date: new Date().toISOString(),
        status: "applied",
        url: url.trim(),
      };

      onAdd(app);
      setUrl("");
      setShowInput(false);

      // Auto-open edit mode if extraction was incomplete
      if (!company || !position || company === "Unknown Company" || position === "Unknown Position") {
        setEditingId(app.id);
        setEditCompany(app.company === "Unknown Company" ? "" : app.company);
        setEditPosition(app.position === "Unknown Position" ? "" : app.position);
        toast.info("We couldn't fully extract the job details — please edit below.");
      } else {
        toast.success(`Added ${app.position} at ${app.company}`);
      }
    } catch (err: any) {
      console.error("Extract error:", err);

      // Still add the job — let the user fill in details manually
      const app: Application = {
        id: generateId(),
        company: "Enter job details manually",
        position: "Error fetching job",
        date: new Date().toISOString(),
        status: "applied",
        url: url.trim(),
      };

      onAdd(app);
      setUrl("");
      setShowInput(false);

      // Auto-open edit mode so user can fix it immediately
      setEditingId(app.id);
      setEditCompany("");
      setEditPosition("");
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (app: Application) => {
    setEditingId(app.id);
    setEditCompany(app.company);
    setEditPosition(app.position);
  };

  const saveEdit = () => {
    if (!editingId) return;
    onUpdateApp(editingId, {
      company: editCompany.trim() || "Unknown Company",
      position: editPosition.trim() || "Unknown Position",
    });
    setEditingId(null);
    toast.success("Updated");
  };

  const saveCtc = (id: string) => {
    onUpdateApp(id, { ctc: editCtc.trim() });
    setEditingCtcId(null);
  };

  const startEditingCtc = (app: Application) => {
    setEditingCtcId(app.id);
    setEditCtc(app.ctc || "");
  };

  return (
    <div className="p-5 space-y-4 animate-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-sm text-foreground">
          {applications.length > 0
            ? `${applications.length} Application${applications.length !== 1 ? "s" : ""}`
            : "Track Applications"}
        </h3>
        {!showInput && (
          <Button variant="ghost" size="sm" className="gap-1.5 text-xs h-7" onClick={() => setShowInput(true)}>
            <Plus className="w-3.5 h-3.5" /> Add Job
          </Button>
        )}
      </div>

      {/* URL Input */}
      {showInput && (
        <div className="p-3 rounded-xl bg-muted/50 border border-border space-y-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link2 className="w-3.5 h-3.5" />
            Paste a job listing URL
          </div>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://careers.company.com/job/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="text-sm h-9"
              onKeyDown={(e) => e.key === "Enter" && handleExtract()}
              disabled={loading}
              autoFocus
            />
            <Button size="sm" className="h-9 px-3 shrink-0" onClick={handleExtract} disabled={!url.trim() || loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-9 px-2 shrink-0"
              onClick={() => {
                setShowInput(false);
                setUrl("");
              }}
              disabled={loading}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            We'll auto-extract the company &amp; position. You can edit them after.
          </p>
        </div>
      )}

      {/* Empty state */}
      {applications.length === 0 && !showInput && (
        <div className="text-center py-12">
          <Briefcase className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No applications tracked yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Paste a job listing URL to start tracking.
          </p>
          <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={() => setShowInput(true)}>
            <Plus className="w-3.5 h-3.5" /> Add Your First Job
          </Button>
        </div>
      )}

      {/* Application cards */}
      {applications.map((app) => (
        <div key={app.id} className="p-4 rounded-xl bg-card border border-border space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              {editingId === app.id ? (
                <div className="space-y-2">
                  <Input
                    value={editPosition}
                    onChange={(e) => setEditPosition(e.target.value)}
                    placeholder="Job title / position"
                    className="text-sm h-8"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                  />
                  <Input
                    value={editCompany}
                    onChange={(e) => setEditCompany(e.target.value)}
                    placeholder="Company name"
                    className="text-sm h-8"
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                  />
                </div>
              ) : (
                <>
                  <div className="font-medium text-[15px] text-foreground truncate">{app.position}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{app.company}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(app.date).toLocaleDateString()}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {editingId === app.id ? (
                <button
                  onClick={saveEdit}
                  className="text-primary hover:text-primary/80 transition-colors p-1"
                >
                  <Check className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => startEditing(app)}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
              {app.url && editingId !== app.id && (
                <a
                  href={app.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors p-1"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button
                onClick={() => onRemove(app.id)}
                className="text-muted-foreground hover:text-destructive transition-colors p-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Status selector */}
          {editingId !== app.id && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {STATUS_OPTIONS.map((status) => (
                <button
                  key={status}
                  onClick={() => onUpdateStatus(app.id, status)}
                  className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full transition-all ${
                    app.status === status
                      ? STATUS_COLORS[status]
                      : "bg-muted/50 text-muted-foreground/50 hover:text-muted-foreground"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          )}

          {/* CTC Toggle & Work Mode */}
          {editingId !== app.id && (
            <div className="mt-4 space-y-4">
              {/* CTC */}
              <div className="flex items-center gap-2 py-1">
                <span className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
                  <span>{CURRENCY_SYMBOLS[settings.currency || 'USD']}</span>
                  CTC:
                </span>
                {editingCtcId === app.id ? (
                  <>
                    <Input
                      value={editCtc}
                      onChange={(e) => setEditCtc(e.target.value)}
                      placeholder={`e.g. 80,000`}
                      className="h-7 text-xs w-28 px-2"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveCtc(app.id);
                        if (e.key === "Escape") setEditingCtcId(null);
                      }}
                    />
                    <button onClick={() => saveCtc(app.id)} className="text-primary hover:text-primary/80 transition-colors p-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => startEditingCtc(app)}
                    className="flex items-center gap-1 text-[10px] font-semibold text-foreground hover:text-primary transition-colors group"
                  >
                    <span>{app.ctc ? `${app.ctc} ${settings.currency || 'USD'}` : "0.00"}</span>
                    <Pencil className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
                  </button>
                )}
              </div>

              {/* Work mode tags */}
              <div className="flex gap-2">
                {([
                  { value: 'onsite' as const, label: 'Onsite', icon: Building2 },
                  { value: 'hybrid' as const, label: 'Hybrid', icon: Home },
                  { value: 'remote' as const, label: 'Remote', icon: MapPin },
                ]).map(mode => (
                  <button
                    key={mode.value}
                    onClick={() => onUpdateApp(app.id, { workMode: app.workMode === mode.value ? undefined : mode.value })}
                    className={`flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 rounded-full transition-all ${
                      app.workMode === mode.value
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted/50 text-muted-foreground/50 hover:text-muted-foreground'
                    }`}
                  >
                    <mode.icon className="w-3 h-3" />
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
